import { useMemo } from "react";
import { Loader2Icon } from "lucide-react";
import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { cn } from "~/lib/utils";
import { Skeleton } from "~/components/ui/skeleton";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { buildTaskThreadIndex } from "~/lib/taskThreadLink";
import { summarizeTasks } from "./taskSummary";

export interface ProjectTaskSummaryProps {
  readonly tasks: readonly CmmdBoardTask[];
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly loading: boolean;
  /**
   * overview = borderless progress strip for the project decision rail.
   * default = card chrome for denser surfaces.
   */
  readonly density?: "default" | "overview";
}

/** Overview roll-up of a project's tasks: progress, counts, active agents. */
export function ProjectTaskSummary({
  tasks,
  threadSummaries,
  loading,
  density = "default",
}: ProjectTaskSummaryProps) {
  const index = useMemo(() => buildTaskThreadIndex(threadSummaries), [threadSummaries]);
  const summary = useMemo(() => summarizeTasks(tasks, index), [tasks, index]);
  const overview = density === "overview";

  if (loading) {
    return overview ? (
      <div aria-label="Loading tasks" className="space-y-2">
        <Skeleton className="h-4 w-20 rounded" />
        <Skeleton className="h-1 rounded-full" />
      </div>
    ) : (
      <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        Loading tasks…
      </div>
    );
  }
  if (summary.total === 0) {
    return overview ? (
      <p className="text-xs text-muted-foreground/70">No tasks yet</p>
    ) : (
      <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        No tasks yet. Open the Tasks tab to add one.
      </div>
    );
  }

  const statusLine = summary.byStatus.map((s) => `${s.count} ${s.label}`).join(" · ");

  if (overview) {
    return (
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm text-foreground">
            <span className="font-medium tabular-nums">{summary.done}</span>
            <span className="text-muted-foreground"> / {summary.total} done</span>
          </p>
          {summary.activeAgents > 0 ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-hue-sky-foreground">
              <Loader2Icon className="size-3 animate-spin" aria-hidden />
              {summary.activeAgents} active
            </span>
          ) : null}
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-success/70 transition-[width] duration-300"
            style={{ width: `${Math.round(summary.progress * 100)}%` }}
          />
        </div>
        <p className="text-[11px] leading-4 text-muted-foreground/75 tabular-nums">{statusLine}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">
          <span className="tabular-nums">{summary.done}</span>
          <span className="text-muted-foreground"> / {summary.total} done</span>
        </span>
        {summary.activeAgents > 0 ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-hue-sky-foreground">
            <Loader2Icon className="size-3 animate-spin" aria-hidden />
            {summary.activeAgents} agent{summary.activeAgents === 1 ? "" : "s"} active
          </span>
        ) : null}
      </div>

      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-success/70 transition-[width] duration-300"
          style={{ width: `${Math.round(summary.progress * 100)}%` }}
        />
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {summary.byStatus.map((s) => (
          <span
            key={s.status}
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <span
              className={cn("size-1.5 rounded-full", getStatusConfig(s.status).dotColor)}
              aria-hidden
            />
            {s.label}
            <span className="tabular-nums text-foreground/70">{s.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
